/**
 * Stable React keys across the block_id lifecycle.
 *
 * A brand-new client-written paragraph starts with block_id=null
 * (textBlockId → '_paragraph_42'); after the CRDT roundtrip the server
 * stamps a block_id and textBlockId flips to it. If the React key
 * followed that flip, React would unmount+mount the card — a new DOM
 * element — and the FLIP position-transition would silently not fire
 * at exactly the moment the user wants it (their own insert gliding
 * into place). Tree IACP 0de6d3dc.
 *
 * Strategy: a persistent signature map mints ONE key per card and
 * returns it before and after the block_id arrives.
 *   P1  lookup by block_id (fast path once backfilled)
 *   P2  lookup by content-signature (author + first 50 chars); on hit
 *       with a fresh block_id, backfill P1
 *   P3  mint (block_id if present, else the signature itself)
 *
 * Collision guard (Tree IACP 49434207): two empty cards from the same
 * author in one render would resolve to the same signature key —
 * React key-duplication drops/duplicates children. A per-render
 * usedKeys set suffixes later occurrences deterministically (::dupN by
 * render position), so the same N-th colliding card keeps its suffix
 * across renders.
 */

import { useMemo, useState } from 'react'
import { textBlockId } from '@arkturian/canvas-engine'
import type { CanvasBlock } from './mergeBlocks'

export function useStableCardKeys(
  blocks: CanvasBlock[],
): Map<string, string> {
  // Mount-stable cache instance (useState lazy init, never re-set).
  // Mutating it during the memo is idempotent — same inputs mint the
  // same entries — so strict-mode double-invocation is safe.
  const [stableKeyMap] = useState(() => new Map<string, string>())

  return useMemo(() => {
    const result = new Map<string, string>()
    const usedInThisRender = new Set<string>()
    for (const b of blocks) {
      let key: string | null = null
      if (b.block_id) {
        key = stableKeyMap.get(`id::${b.block_id}`) ?? null
      }
      if (!key) {
        const text50 = (b.text_preview || '').slice(0, 50)
        const sig = `sig::${b.author?.id || ''}::${text50}`
        const fromSig = stableKeyMap.get(sig)
        if (fromSig) {
          if (b.block_id) {
            stableKeyMap.set(`id::${b.block_id}`, fromSig)
          }
          key = fromSig
        } else {
          const newKey = b.block_id ?? sig
          stableKeyMap.set(sig, newKey)
          if (b.block_id) {
            stableKeyMap.set(`id::${b.block_id}`, newKey)
          }
          key = newKey
        }
      }
      let candidate = key
      let n = 0
      while (usedInThisRender.has(candidate)) {
        n++
        candidate = `${key}::dup${n}`
      }
      usedInThisRender.add(candidate)
      result.set(textBlockId(b), candidate)
    }
    return result
  }, [blocks, stableKeyMap])
}
